/**
 * Hand a case back to today's flow: it leaves the workbench for good. Open
 * tasks are cancelled and open asks closed; the latest workbench draft
 * becomes its next content version. Today's flow carries on untouched.
 */

import { useState } from 'react';

import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowBackUp } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { toError } from '@/utils/errors';

const COPY = {
  button: "Hand back to today's flow",
  title: "Hand this case back to today's flow?",
  body:
    "Open tasks are cancelled, open asks close, and the workbench's latest draft (if " +
    'any) is saved as the next content version. You carry on in the report page and ' +
    'Backoffice. The case leaves the workbench for good.',
};

interface HandBackButtonProps {
  reportId: string;
  onDone: () => void | Promise<void>;
}

export function HandBackButton({ reportId, onDone }: Readonly<HandBackButtonProps>) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const copy = COPY;

  const confirm = async () => {
    setBusy(true);
    try {
      const result = await vnextApiClient.handBackCase(reportId, reason.trim());
      notifications.show({
        color: 'teal',
        message:
          result.contentVersion !== null
            ? `Handed back — the draft is version ${result.contentVersion}.`
            : "Handed back to today's flow.",
      });
      setOpen(false);
      await onDone();
    } catch (err) {
      Sentry.captureException(toError(err));
      notifications.show({ color: 'red', message: toError(err, 'Could not hand back').message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="xs"
        variant="default"
        leftSection={<IconArrowBackUp size={14} />}
        onClick={() => setOpen(true)}
        data-testid="hand-back"
      >
        {copy.button}
      </Button>
      <Modal opened={open} onClose={() => setOpen(false)} title={copy.title} centered>
        <Stack gap="sm">
          <Text size="sm">{copy.body}</Text>
          <Textarea
            label="Why"
            placeholder="Recorded on the case"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            autosize
            minRows={2}
            required
            data-testid="hand-back-reason"
          />
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="subtle" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="xs"
              color="red"
              disabled={!reason.trim()}
              loading={busy}
              onClick={() => void confirm()}
              data-testid="confirm-hand-back"
            >
              {copy.button}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
